import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, User, Mail, Phone, MapPin, Calendar, Shield, Settings, Search, Filter, ChevronDown, Eye } from 'lucide-react';

const UserManagementDashboard = () => {
  const [users, setUsers] = useState([
    {
      id: 1,
      firstName: 'Rahul',
      lastName: 'Kumar',
      email: 'rahul.kumar@email.com',
      phone: '+91 9876543210',
      role: 'Admin',
      department: 'Sales',
      address: 'Mumbai, Maharashtra',
      dateJoined: '2023-01-15',
      status: 'Active',
      permissions: ['read', 'write', 'delete'],
      preferences: {
        notifications: true,
        theme: 'light',
        language: 'English'
      }
    },
    {
      id: 2,
      firstName: 'Priya',
      lastName: 'Sharma',
      email: 'priya.sharma@email.com',
      phone: '+91 9876543211',
      role: 'Manager',
      department: 'Marketing',
      address: 'Delhi, India',
      dateJoined: '2023-02-20',
      status: 'Active',
      permissions: ['read', 'write'],
      preferences: {
        notifications: false,
        theme: 'dark',
        language: 'Hindi'
      }
    },
    {
      id: 3,
      firstName: 'Amit',
      lastName: 'Patel',
      email: 'amit.patel@email.com',
      phone: '+91 9876543212',
      role: 'User',
      department: 'IT',
      address: 'Bangalore, Karnataka',
      dateJoined: '2023-03-10',
      status: 'Inactive',
      permissions: ['read'],
      preferences: {
        notifications: true,
        theme: 'light',
        language: 'English'
      }
    }
  ]);

  const [currentView, setCurrentView] = useState('list'); // 'list', 'create', 'edit', 'view'
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'User',
    department: '',
    address: '',
    status: 'Active',
    permissions: [],
    preferences: {
      notifications: true,
      theme: 'light',
      language: 'English'
    }
  });

  const roles = ['Admin', 'Manager', 'User', 'Viewer'];
  const departments = ['Sales', 'Marketing', 'HR', 'IT', 'Finance', 'Operations'];
  const permissionsList = ['read', 'write', 'delete', 'admin'];
  const themes = ['light', 'dark'];
  const languages = ['English', 'Hindi', 'Gujarati', 'Marathi'];
  const statusOptions = ['Active', 'Inactive', 'Suspended'];

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.department.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'All' || user.role === filterRole;
    const matchesStatus = filterStatus === 'All' || user.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'User',
      department: '',
      address: '',
      status: 'Active',
      permissions: [],
      preferences: {
        notifications: true,
        theme: 'light',
        language: 'English'
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('preferences.')) {
      const prefKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name === 'permissions') {
      const updatedPermissions = checked 
        ? [...formData.permissions, value]
        : formData.permissions.filter(p => p !== value);
      setFormData(prev => ({ ...prev, permissions: updatedPermissions }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreateUser = () => {
    if (formData.firstName && formData.lastName && formData.email) {
      const newUser = {
        ...formData,
        id: Math.max(...users.map(u => u.id)) + 1,
        dateJoined: new Date().toISOString().split('T')[0]
      };
      setUsers(prev => [...prev, newUser]);
      resetForm();
      setCurrentView('list');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({ ...user });
    setCurrentView('edit');
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setCurrentView('view');
  };

  const handleUpdateUser = () => {
    if (formData.firstName && formData.lastName && formData.email) {
      setUsers(prev => prev.map(user => 
        user.id === editingUser.id ? { ...formData, id: editingUser.id } : user
      ));
      resetForm();
      setEditingUser(null);
      setCurrentView('list');
    }
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(prev => prev.filter(user => user.id !== userId));
    }
  };

  const renderUserForm = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <User className="text-blue-600" />
          {currentView === 'create' ? 'Create New User' : 'Update User'}
        </h2>
        <button
          onClick={() => {
            setCurrentView('list');
            resetForm();
            setEditingUser(null);
          }}
          className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100"
        >
          <X size={24} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Personal Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter last name"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Mail size={16} /> Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Phone size={16} /> Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <MapPin size={16} /> Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter complete address"
            />
          </div>
        </div>

        {/* Professional Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Professional Information</h3>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Shield size={16} /> Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permissions
            </label>
            <div className="grid grid-cols-2 gap-2">
              {permissionsList.map(permission => (
                <label key={permission} className="flex items-center p-2 rounded-md hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    name="permissions"
                    value={permission}
                    checked={formData.permissions.includes(permission)}
                    onChange={handleInputChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{permission}</span>
                </label>
              ))}
            </div>
          </div>

          {/* User Preferences */}
          <div>
            <h4 className="text-md font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Settings size={16} /> User Preferences
            </h4>
            
            <div className="space-y-3">
              <label className="flex items-center p-2 rounded-md hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  name="preferences.notifications"
                  checked={formData.preferences.notifications}
                  onChange={handleInputChange}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Enable Notifications</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Theme Preference
                </label>
                <select
                  name="preferences.theme"
                  value={formData.preferences.theme}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  {themes.map(theme => (
                    <option key={theme} value={theme}>
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language Preference
                </label>
                <select
                  name="preferences.language"
                  value={formData.preferences.language}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  {languages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => {
            setCurrentView('list');
            resetForm();
            setEditingUser(null);
          }}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        
        <button
          onClick={currentView === 'create' ? handleCreateUser : handleUpdateUser}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
        >
          <Save size={16} />
          {currentView === 'create' ? 'Create User' : 'Update User'}
        </button>
      </div>
    </div>
  );

  const renderUserView = () => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <User className="text-blue-600" />
          User Details
        </h2>
        <button
          onClick={() => setCurrentView('list')}
          className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-100"
        >
          <X size={24} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Personal Information</h3>
          
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-4">
              <User className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{viewingUser.firstName} {viewingUser.lastName}</h2>
              <p className="text-gray-600">{viewingUser.department}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-800 flex items-center gap-1">
                <Mail size={14} /> {viewingUser.email}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-gray-800 flex items-center gap-1">
                <Phone size={14} /> {viewingUser.phone}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-500">Address</p>
              <p className="text-gray-800 flex items-center gap-1">
                <MapPin size={14} /> {viewingUser.address}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Professional Information</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Role</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                viewingUser.role === 'Admin' ? 'bg-red-100 text-red-800' :
                viewingUser.role === 'Manager' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {viewingUser.role}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Department</p>
              <p className="text-gray-800">{viewingUser.department}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                viewingUser.status === 'Active' ? 'bg-green-100 text-green-800' :
                viewingUser.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                'bg-red-100 text-red-800'
              }`}>
                {viewingUser.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Join Date</p>
              <p className="text-gray-800 flex items-center gap-1">
                <Calendar size={14} /> 
                {new Date(viewingUser.dateJoined).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-1">Permissions</p>
            <div className="flex flex-wrap gap-2">
              {viewingUser.permissions.map(permission => (
                <span key={permission} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full capitalize">
                  {permission}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-md font-semibold text-gray-700 mb-2">Preferences</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Theme</p>
                <p className="text-gray-800 capitalize">{viewingUser.preferences.theme}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Language</p>
                <p className="text-gray-800">{viewingUser.preferences.language}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Notifications</p>
                <p className="text-gray-800">{viewingUser.preferences.notifications ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => setCurrentView('list')}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back to List
        </button>
        <button
          onClick={() => handleEditUser(viewingUser)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Edit size={16} />
          Edit User
        </button>
      </div>
    </div>
  );

  const renderUserList = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
         <div className='flex gap-2'> 

          <button
            onClick={() => {
                resetForm();
                setCurrentView('create');
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <Plus size={16} />
            Create New User
          </button>
          <button
            
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
          >
        Export
          </button>
            </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>
          
          <div className="flex gap-2">
            <div className="relative">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="All">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            </div>
            
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="All">All Status</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role & Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Join Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="text-blue-600" size={16} />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.phone}</div>
                  <div className="text-sm text-gray-500">{user.address}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'Admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'Manager' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                  <div className="text-sm text-gray-500 mt-1">{user.department}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.status === 'Active' ? 'bg-green-100 text-green-800' :
                    user.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(user.dateJoined).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewUser(user)}
                      className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded-md hover:bg-blue-50"
                      title="View User"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded-md hover:bg-blue-50"
                      title="Edit User"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 transition-colors p-1 rounded-md hover:bg-red-50"
                      title="Delete User"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto text-gray-300" size={48} />
            <p className="mt-2 text-gray-500">No users found</p>
            <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center">
        <p className="text-sm text-gray-600 mb-2 sm:mb-0">
          Showing {filteredUsers.length} of {users.length} users
        </p>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            Active: {users.filter(u => u.status === 'Active').length}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            Inactive: {users.filter(u => u.status === 'Inactive').length}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {currentView === 'list' && renderUserList()}
        {(currentView === 'create' || currentView === 'edit') && renderUserForm()}
        {currentView === 'view' && renderUserView()}
      </div>
    </div>
  );
};

export default UserManagementDashboard;